import { Box, Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import log from 'loglevel';
import { HISTORY_ACTION_TYPE } from '../../../consts/enums';
import { FILTER_TYPES, HISTORY_ACTION_TYPE_LABELS } from '../../../consts';
import { FilterItem, FilterOptions } from '../../../interfaces/filterOptions.interface';
import STRING_KEYS from '../../../language/keys';
import { createActiveFilterObject } from '../../../utils';
import MultiSelect from '../../MultiSelect';
import TableFiltersItems from '../../Table/TableRenderFiltersItems';
import { getFullName } from '../utils';
import { style } from './style';

type HistoryFiltersProps = {
  onActivityChanged: (val: string[]) => void,
  onMadeByChanged: (val: string[]) => void,
  data: HistoryLogEntryResponse[],
  activityFilters: string[],
  madeByFilters: string[],
  clearFilters: () => void,
  onUserQueryTextChanged: (query: string) => void,
  userQueryText: string,
  entity: string,
}

export const HistoryFilters = ({
  onActivityChanged,
  onMadeByChanged,
  data,
  activityFilters,
  madeByFilters,
  clearFilters,
  onUserQueryTextChanged,
  userQueryText,
  entity,
}: HistoryFiltersProps):JSX.Element => {
  const TAG = `[${entity}_FILTERS_COMPONENT]`;
  const { t: translate } = useTranslation();
  const [isLoadingActivity] = useState<boolean>(false);
  const [isLoadingMadeBy] = useState<boolean>(false);

  const activityOptions = [
    { label: translate(STRING_KEYS.ADDED), value: HISTORY_ACTION_TYPE.CREATE },
    { label: translate(STRING_KEYS.EDIT), value: HISTORY_ACTION_TYPE.UPDATE },
    { label: translate(STRING_KEYS.REMOVED), value: HISTORY_ACTION_TYPE.REMOVE },
    { label: translate(STRING_KEYS.STATUS_CHANGED), value: HISTORY_ACTION_TYPE.STATUS_CHANGED },
    {
      label: translate(STRING_KEYS.MATCH_PAGE_TAB_ENGAGEMENT),
      value: HISTORY_ACTION_TYPE.ENGAGEMENT,
    },
  ];

  const getOption = (item: HistoryLogEntryResponse):FilterOptions => {
    if (item.user?.firstName) {
      return {
        label: getFullName(item.user),
        value: getFullName(item.user),
      };
    }

    return {
      label: `${item.user.id}`,
      value: `${item.user.id}`,
    };
  };

  const madeByOptions = () => {
    const options:Array<FilterOptions> = [];
    data.forEach((item: HistoryLogEntryResponse) : FilterOptions | undefined => {
      if (options.some((option) => option.value === item.user.id ||
        option.value === getFullName(item.user))) {
        return;
      }

      if (!item.user?.id?.toLowerCase().includes(userQueryText?.toLowerCase())) {
        return;
      }
      options.push(getOption(item));
    });

    return options;
  };

  const getActiveFilters = (): FilterItem[] => {
    const filters: FilterItem[] = [];

    if (activityFilters.length > 0) {
      activityFilters.forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              FILTER_TYPES.ACTIVITIES,
              'Activities',
              HISTORY_ACTION_TYPE_LABELS[item],
            ),
          );
        }
      });
    }

    if (madeByFilters.length > 0) {
      madeByFilters.forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              FILTER_TYPES.MADE_BY,
              'Made By',
              item,
            ),
          );
        }
      });
    }

    return filters;
  };

  const handleDeleteFilter = (filterItem: FilterItem) => {
    switch (filterItem.field) {
      case FILTER_TYPES.ACTIVITIES: {
        onActivityChanged(activityFilters
          .filter((item: string) => {
            return HISTORY_ACTION_TYPE_LABELS[item] !== filterItem.value;
          }));

        break;
      }
      case FILTER_TYPES.MADE_BY: {
        onMadeByChanged(activityFilters
          .filter((item: string) => {
            return item !== filterItem.value;
          }));

        break;
      }
      default:
        log.warn(TAG, `Cannot find any handler for type:${filterItem.field}`);
    }
  };

  return (
    <Box sx={ style.box.sx }>
      <Box sx={ style.box.inner }>
        <Box>
          <MultiSelect
            label={ translate(STRING_KEYS.ACTIVITY_TYPE) }
            onChange={ onActivityChanged }
            options={ activityOptions || [] }
            value={ activityFilters }
            isLoading={ isLoadingActivity }
          />
          <MultiSelect
            label={ translate(STRING_KEYS.MADE_BY) }
            onChange={ onMadeByChanged }
            options={ madeByOptions() || [] }
            value={ madeByFilters }
            autoComplete
            autoCompleteOnChange={ onUserQueryTextChanged }
            autoCompleteValue={ userQueryText }
            isLoading={ isLoadingMadeBy }
          />
        </Box>
        {(activityFilters.length !== 0 || madeByFilters.length !== 0) && (
          <Button variant="text"
            sx={ style.clearBtn }
            onClick={ clearFilters }
          >
            {translate(STRING_KEYS.CLEAR)}
          </Button>
        )}
      </Box>
      <TableFiltersItems filters={ getActiveFilters() } onDelete={ handleDeleteFilter } />
    </Box>
  );
};
